-- | Implements a widget for selecting pages in a paginated list.
module Widget.Pager (pagerWidget) where

import Common

import API.Pagination (Page, pageLast, pageNumber)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array as Arr
import Data.Ord as Ord

-- | A simple widget which takes a `Page` in a paginated collection and renders
-- | a page selection UI. When the user clicks one of the pages, this simply
-- | returns the number of the page the user clicked on. It is up to the user
-- | of this function to load that page and then call this again with the new
-- | page.
pagerWidget :: forall a. Page a -> Widget HTML Int
pagerWidget page | pageLast page <= 1 = D.div' []
pagerWidget page = do
    D.div [ P.className "pager btn-group" ]
          (prevLink <> firstLink <> pageNumLinks <> lastLink <> nextLink)
  where
    pageNo = pageNumber page
    lastPage = pageLast page

    nearNums = nearbyNumbers 10 lastPage pageNo
    pageNumLinks = map pageLinkN nearNums
    pageLinkN n = pageLink n $ show n

    nextLink | pageNo < lastPage = [pageLink (pageNo + 1) $ lookupTranslation "next"]
    nextLink = []
    prevLink | pageNo > 1 = [pageLink (pageNo - 1) $ lookupTranslation "prev"]
    prevLink = []

    -- show first/last page links if one is not already included
    firstLink | 1 `Arr.elem` nearNums = []
    firstLink = [pageLink 1 ("1 ...")]
    lastLink  | lastPage `Arr.elem` nearNums = []
    lastLink  = [pageLink lastPage ("... " <> show lastPage)]

    pageLink n txt = D.button
        [ P.className cls, n <$ P.onClick, P._type "button" ]
        [ D.text txt ]
      where cls | n == pageNo = "btn btn-primary"
            cls = "btn"



-- | Generates a list of `n` consecutive numbers around `near` greater than zero
-- | and less than or equal to `end`.
nearbyNumbers :: Int -> Int -> Int -> Array Int
nearbyNumbers len end _ | len > end = Arr.range 1 end
nearbyNumbers len end near = less <> more
  where
    -- determine where to start. either half the total length away from `near`,
    -- or a minimum of 1.
    startAt = Ord.max 1 (near - len / 2)
    -- add more elements to the end until we're `len` numbers long
    endLen = len - (near - startAt)
    endAt = Ord.min end (near + endLen - 1)
    -- construct the lists
    less | near > 1 = Arr.range startAt (near - 1)
    less = []
    more = Arr.range near endAt

